import { useEffect, useLayoutEffect, useState } from 'react'
import { HAVE_WINDOW } from 'src/constants'

const DEFAULT_MIN_UNITS = 2
const DEFAULT_MAX_UNITS = 6
const STORAGE_KEY = 'dy-last-units-counter-by-product'

const DEFAULT_COUNTER_STATE: CounterState = {
  showCounterVersion: false,
  unitsLeft: null,
}

const useIsomorphicLayoutEffect = HAVE_WINDOW ? useLayoutEffect : useEffect

type LastUnitsCounterByProduct = Record<string, number>

type CounterState = {
  showCounterVersion: boolean
  unitsLeft: number | null
}

const getRandomIntegerInRange = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min

const getSanitizedRangeValue = (value: unknown, fallbackValue: number) => {
  const parsedValue = Number(value)

  if (!Number.isFinite(parsedValue)) {
    return fallbackValue
  }

  return Math.trunc(parsedValue)
}

const isValidPersistedValue = (
  value: unknown,
  minRange: number,
  maxRange: number
): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= minRange &&
  value <= maxRange

const getStoredCounterByProduct = (): LastUnitsCounterByProduct => {
  try {
    const rawValue = window.sessionStorage.getItem(STORAGE_KEY)

    if (!rawValue) {
      return {}
    }

    const parsedValue = JSON.parse(rawValue)

    return typeof parsedValue === 'object' && parsedValue !== null
      ? (parsedValue as LastUnitsCounterByProduct)
      : {}
  } catch {
    return {}
  }
}

const persistCounterForProduct = (productId: string, unitsLeft: number) => {
  try {
    const persistedValues = getStoredCounterByProduct()

    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...persistedValues,
        [productId]: unitsLeft,
      })
    )
  } catch {
    // Intencionalmente silencioso para não quebrar UX se storage estiver indisponível.
  }
}

const getUnitsLeft = (
  productId: string | undefined,
  minRange: number,
  maxRange: number
) => {
  if (!productId) {
    return getRandomIntegerInRange(minRange, maxRange)
  }

  const persistedValues = getStoredCounterByProduct()
  const persistedValue = persistedValues[productId]

  if (isValidPersistedValue(persistedValue, minRange, maxRange)) {
    return persistedValue
  }

  const randomValue = getRandomIntegerInRange(minRange, maxRange)

  persistCounterForProduct(productId, randomValue)

  return randomValue
}

const getCounterState = (
  productId: string | undefined,
  enabled: boolean
): CounterState => {
  if (!HAVE_WINDOW || !enabled) {
    return DEFAULT_COUNTER_STATE
  }

  const dyConfig = window.dynamicYieldUtils?.lastUnitsFlag

  if (!dyConfig?.isCounterVersionEnabled) {
    return DEFAULT_COUNTER_STATE
  }

  const minUnits = getSanitizedRangeValue(dyConfig.minUnits, DEFAULT_MIN_UNITS)
  const maxUnits = getSanitizedRangeValue(dyConfig.maxUnits, DEFAULT_MAX_UNITS)
  const minRange = Math.min(minUnits, maxUnits)
  const maxRange = Math.max(minUnits, maxUnits)

  return {
    showCounterVersion: true,
    unitsLeft: getUnitsLeft(productId, minRange, maxRange),
  }
}

export function useLastUnitsCounterFlag(productId?: string, enabled = true) {
  const [counterState, setCounterState] = useState<CounterState>(
    DEFAULT_COUNTER_STATE
  )

  useIsomorphicLayoutEffect(() => {
    setCounterState(getCounterState(productId, enabled))
  }, [productId, enabled])

  return {
    showCounterVersion: counterState.showCounterVersion,
    unitsLeft: counterState.unitsLeft,
  }
}
